import React, { lazy, Suspense, useEffect, ErrorInfo } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import store from './store'
import { Indicators, Templates } from 'ui'
import { Toaster } from 'react-hot-toast'

const TOAST_STYLE = {
  border: '3px solid #F26724',
  background: '#FFFFFF',
  color: '#132048',
  borderRadius: '0',
  boxShadow: '6px 6px 0 rgba(19, 32, 72, 0.35)',
  padding: '12px 16px',
}

// App-level error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null; errorInfo: ErrorInfo | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error) {
    console.error('App ErrorBoundary caught error:', error)
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App ErrorBoundary error details:')
    console.error('Error message:', error.message)
    console.error('Component stack:', errorInfo.componentStack)

    this.setState({
      error,
      errorInfo,
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 20,
            maxWidth: '800px',
            margin: '40px auto',
            border: '1px solid #ccc',
            borderRadius: '8px',
            backgroundColor: '#f8f8f8',
          }}
        >
          <h2 style={{ color: '#e53935' }}>Application Error</h2>
          <p>
            The application encountered an error and could not continue. Please
            try refreshing the page.
          </p>

          <details style={{ marginTop: 20 }}>
            <summary style={{ cursor: 'pointer', color: '#333' }}>
              Error details
            </summary>
            <div
              style={{
                padding: 10,
                marginTop: 10,
                backgroundColor: '#eee',
                borderRadius: '4px',
              }}
            >
              <h4>Error Message:</h4>
              <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
                {this.state.error && this.state.error.toString()}
              </pre>

              {this.state.errorInfo && (
                <>
                  <h4>Component Stack:</h4>
                  <pre style={{ overflow: 'auto', maxHeight: '200px' }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </>
              )}
            </div>
          </details>
        </div>
      )
    }

    return this.props.children
  }
}

// Use a safer approach for lazy loading
const RootRouter = lazy(() => {
  console.log('App.tsx: Attempting to load RootRouter')
  return import('./routes')
    .then((module) => {
      console.log('App.tsx: RootRouter loaded successfully')
      return module
    })
    .catch((error) => {
      console.error('App.tsx: Error loading RootRouter:', error)
      // Return a fallback component instead of throwing
      return {
        default: () => (
          <div style={{ padding: 20 }}>
            <h2>Error Loading Routes</h2>
            <p>There was an error loading the application routes.</p>
            <details>
              <summary>Error details</summary>
              <pre>{String(error)}</pre>
            </details>
          </div>
        ),
      }
    })
})

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      onError: (err) => {
        console.error('React Query error:', err)
      },
    },
  },
})

/**
 * The root component for this application. Sets up any appropriate
 * providers and enforces strict HTTPS.
 */
const App: React.FC = () => {
  useEffect(() => {
    // Force HTTPS
    if (
      window.location.protocol.indexOf('https') < 0 &&
      window.location.hostname !== 'localhost'
    ) {
      console.log('App.tsx: Redirecting to HTTPS version')
      window.location.href = `https://${window.location.hostname}${window.location.pathname}`
    }
  }, [])

  console.log('App.tsx: Rendering provider tree')
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <HelmetProvider>
            <DndProvider backend={HTML5Backend}>
              <Suspense fallback={<Indicators.FullScreen />}>
                <ErrorBoundary>
                  <Toaster
                    position="top-right"
                    reverseOrder={true}
                    toastOptions={{
                      className: 'mcss-toast',
                      style: TOAST_STYLE,
                      success: {
                        iconTheme: {
                          primary: '#F26724',
                          secondary: '#FFFFFF',
                        },
                      },
                      error: {
                        iconTheme: {
                          primary: '#F26724',
                          secondary: '#FFFFFF',
                        },
                      },
                    }}
                  />
                  <ErrorBoundary>
                    <RootRouter />
                  </ErrorBoundary>
                  <ReactQueryDevtools initialIsOpen={false} />
                </ErrorBoundary>
              </Suspense>
            </DndProvider>
          </HelmetProvider>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
